import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import { DollarSign, TrendingUp, TrendingDown, Receipt, Plus, Trash2, Scissors, Users, Repeat } from "lucide-react";

const COLORS = ["#D4AF37", "#3B82F6", "#22C55E", "#EF4444", "#F59E0B", "#8B5CF6"];
const CATS = ["Aluguel", "Energia", "Água", "Internet", "Salários", "Produtos", "Manutenção", "Impostos", "Outros"];

function Kpi({ icon: Icon, label, value, accent, testid, sub }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">{label}</span><div className={`w-9 h-9 rounded-full flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div></div>
      <div className="text-xl font-bold text-white mt-3">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Financial() {
  const today = new Date().toISOString().slice(0, 10);
  const [sum, setSum] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [open, setOpen] = useState(false);
  const [fxOpen, setFxOpen] = useState(false);
  const [form, setForm] = useState({ category: "Aluguel", description: "", amount: 0, date: today });
  const [fxEditing, setFxEditing] = useState(null);
  const [fxForm, setFxForm] = useState({ category: "Aluguel", description: "", amount: 0, active: true });

  const load = () => {
    api.get("/financial/summary").then((r) => setSum(r.data));
    api.get("/expenses").then((r) => setExpenses(r.data || []));
    api.get("/fixed-expenses").then((r) => setFixed(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const addExpense = async () => {
    try { await api.post("/expenses", { ...form, amount: Number(form.amount) }); toast.success("Despesa registrada"); setOpen(false); setForm({ category: "Aluguel", description: "", amount: 0, date: today }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeExpense = async (id) => { try { await api.delete(`/expenses/${id}`); load(); } catch {} };

  const openNewFixed = () => { setFxEditing(null); setFxForm({ category: "Aluguel", description: "", amount: 0, active: true }); setFxOpen(true); };
  const openEditFixed = (f) => { setFxEditing(f); setFxForm({ category: f.category, description: f.description || "", amount: f.amount, active: f.active }); setFxOpen(true); };
  const saveFixed = async () => {
    const payload = { ...fxForm, amount: Number(fxForm.amount) };
    try {
      if (fxEditing) await api.put(`/fixed-expenses/${fxEditing.id}`, payload);
      else await api.post("/fixed-expenses", payload);
      toast.success("Despesa fixa salva"); setFxOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const removeFixed = async (id) => { try { await api.delete(`/fixed-expenses/${id}`); load(); } catch {} };
  const toggleFixed = async (f) => { try { await api.put(`/fixed-expenses/${f.id}`, { category: f.category, description: f.description || "", amount: f.amount, active: !f.active }); load(); } catch {} };

  if (!sum) return <p className="text-zinc-400">Carregando...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Financeiro</h1><p className="text-zinc-400 mt-1">Faturamento, despesas e lucro líquido.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" /> Despesa</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={DollarSign} label="Faturamento Dia" value={money(sum.revenue_today)} accent="bg-green-500/15 text-green-400" testid="fin-day" />
        <Kpi icon={TrendingUp} label="Faturamento Semana" value={money(sum.revenue_week)} accent="bg-blue-500/15 text-blue-400" testid="fin-week" />
        <Kpi icon={TrendingUp} label="Faturamento Mês" value={money(sum.revenue_month)} accent="bg-amber-500/15 text-amber-400" testid="fin-month" />
        <Kpi icon={Receipt} label="Ticket Médio" value={money(sum.ticket_medio)} accent="bg-purple-500/15 text-purple-400" testid="fin-ticket" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Scissors} label="Serviços (mês)" value={sum.count_month} accent="bg-cyan-500/15 text-cyan-400" testid="fin-count-month" />
        <Kpi icon={Users} label="Total realizados" value={sum.total_completed} accent="bg-zinc-500/15 text-zinc-300" testid="fin-total" />
        <Kpi icon={TrendingDown} label="Despesas (mês)" value={money(sum.expenses_month)} accent="bg-red-500/15 text-red-400" testid="fin-expenses" sub={`Fixas ${money(sum.expenses_fixed || 0)} + Avulsas ${money(sum.expenses_oneoff || 0)}`} />
        <Kpi icon={DollarSign} label="Lucro Líquido (mês)" value={money(sum.profit_month)} accent={sum.profit_month >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"} testid="fin-profit" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4">Faturamento (últimos 7 dias)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={sum.daily_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="label" stroke="#71717a" fontSize={12} />
              <YAxis stroke="#71717a" fontSize={12} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} formatter={(v) => money(v)} />
              <Bar dataKey="revenue" fill="#D4AF37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4">Serviços mais realizados (mês)</h2>
          {sum.top_services.length === 0 ? <p className="text-zinc-500 py-16 text-center">Sem dados ainda.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={sum.top_services} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {sum.top_services.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4">Desempenho por Barbeiro (mês)</h2>
          {sum.by_barber.length === 0 ? <p className="text-zinc-500 py-8 text-center">Sem dados ainda.</p> : (
            <div className="space-y-3">
              {sum.by_barber.map((b) => (
                <div key={b.barber_name} className="p-4 rounded-xl bg-zinc-800/50" data-testid={`barber-perf-${b.barber_name}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-white font-semibold">{b.barber_name}</span>
                    <span className="text-amber-400 font-bold">{money(b.revenue)}</span>
                  </div>
                  <div className="text-sm text-zinc-400 mt-1">{b.count} serviços • {Object.entries(b.services).map(([k, v]) => `${v} ${k}`).join(", ")}</div>
                  {b.commission_pct > 0 ? (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50 grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-zinc-500">Comissão ({b.commission_pct}%)</span><div className="text-green-400 font-semibold">{money(b.commission_value)}</div></div>
                      <div><span className="text-zinc-500">Fica p/ barbearia</span><div className="text-amber-300 font-semibold">{money(b.net_value)}</div></div>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-zinc-600">Sem comissão configurada (0%). Defina em Barbeiros.</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4">Despesas avulsas</h2>
          {expenses.length === 0 ? <p className="text-zinc-500 py-8 text-center">Nenhuma despesa registrada.</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {expenses.map((e) => (
                <div key={e.id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50" data-testid={`expense-${e.id}`}>
                  <div><div className="text-white text-sm font-medium">{e.category}</div><div className="text-xs text-zinc-500">{e.description} • {new Date(e.date + "T00:00:00").toLocaleDateString("pt-BR")}</div></div>
                  <div className="flex items-center gap-3"><span className="text-red-400 font-semibold">{money(e.amount)}</span><Button size="icon" variant="ghost" className="text-zinc-500 hover:text-red-400 h-7 w-7" onClick={() => removeExpense(e.id)} data-testid={`delete-expense-${e.id}`}><Trash2 className="w-4 h-4" /></Button></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FIXED EXPENSES */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Repeat className="w-5 h-5 text-amber-400" />
            <h2 className="font-heading text-lg font-bold text-white">Despesas Fixas (descontadas todo mês)</h2>
          </div>
          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-100 hover:bg-amber-500/10" onClick={openNewFixed} data-testid="add-fixed-btn"><Plus className="w-4 h-4 mr-1" /> Nova fixa</Button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Essas despesas são somadas automaticamente ao total de despesas do mês e abatidas do lucro — sem precisar lançar de novo todo mês. Desative para parar o desconto.</p>
        {fixed.length === 0 ? (
          <p className="text-zinc-500 py-6 text-center">Nenhuma despesa fixa cadastrada.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fixed.map((f) => (
              <div key={f.id} className={`flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 ${f.active ? "" : "opacity-50"}`} data-testid={`fixed-${f.id}`}>
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium">{f.category} {!f.active && <span className="text-xs text-zinc-500">(inativa)</span>}</div>
                  <div className="text-xs text-zinc-500 truncate">{f.description}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-red-400 font-semibold">{money(f.amount)}</span>
                  <Switch checked={f.active} onCheckedChange={() => toggleFixed(f)} data-testid={`fixed-toggle-${f.id}`} />
                  <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-amber-400 h-7 w-7" onClick={() => openEditFixed(f)} data-testid={`edit-fixed-${f.id}`}><Receipt className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-zinc-500 hover:text-red-400 h-7 w-7" onClick={() => removeFixed(f.id)} data-testid={`delete-fixed-${f.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle className="text-white">Nova Despesa Avulsa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-300">Descrição</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="expense-description" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-300">Valor (R$)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="expense-amount" /></div>
              <div><Label className="text-zinc-300">Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="expense-date" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={addExpense} className="bg-amber-400 text-black font-bold" data-testid="save-expense-btn">Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fxOpen} onOpenChange={setFxOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader><DialogTitle className="text-white">{fxEditing ? "Editar" : "Nova"} Despesa Fixa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Categoria</Label>
              <Select value={fxForm.category} onValueChange={(v) => setFxForm({ ...fxForm, category: v })}>
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="fixed-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-zinc-300">Descrição</Label><Input value={fxForm.description} onChange={(e) => setFxForm({ ...fxForm, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="fixed-description" /></div>
            <div><Label className="text-zinc-300">Valor mensal (R$)</Label><Input type="number" value={fxForm.amount} onChange={(e) => setFxForm({ ...fxForm, amount: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="fixed-amount" /></div>
            <div className="flex items-center gap-3"><Switch checked={fxForm.active} onCheckedChange={(v) => setFxForm({ ...fxForm, active: v })} data-testid="fixed-active" /><Label className="text-zinc-300">Ativa (descontar todo mês)</Label></div>
          </div>
          <DialogFooter><Button onClick={saveFixed} className="bg-amber-400 text-black font-bold" data-testid="save-fixed-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
